import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FolderOpen, PackageCheck, Send, Sparkles, Wallet } from "lucide-react";
import { EmptyState, ProjectRow } from "./components";
import { PLAN_NAMES, firstNameFromEmail } from "./shared";

export default function OverviewPage() {
  const { customer, briefs } = useOutletContext();
  const navigate = useNavigate();

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  const activeBriefs = briefs.filter((b) => b.status !== "Delivered").slice(0, 5);
  const deliveredBriefs = briefs
    .filter((b) => b.status === "Delivered")
    .sort((a, b) => new Date(b.delivered_at || b.created_at) - new Date(a.delivered_at || a.created_at))
    .slice(0, 3);
  const outOfCredits = customer && customer.credits_remaining <= 0;
  const usedPct =
    customer && customer.credits_total > 0
      ? Math.round(((customer.credits_total - customer.credits_remaining) / customer.credits_total) * 100)
      : 0;

  return (
    <div data-testid="overview-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">
          Grapho Studio
        </p>
        <h1 data-testid="overview-greeting" className="mt-2 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
          {greeting}, {firstNameFromEmail(customer.email)}.
        </h1>
        <p className="mt-1 text-sm text-slate-400">Your design team is ready when you are.</p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link
            to="/dashboard/new"
            data-testid="overview-submit-brief-button"
            className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-extrabold uppercase tracking-wide text-[#1A1204] transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-300 hover:shadow-[0_0_32px_rgba(245,158,11,0.5)]"
          >
            <Send className="h-4 w-4" />
            + Submit New Brief
          </Link>
          <Link
            to="/dashboard/projects"
            data-testid="overview-view-projects-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-300 transition-colors hover:text-amber-300"
          >
            View Projects
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </motion.div>

      {/* Compact credit summary */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.08 }}
        data-testid="overview-credits-card"
        className="glass mt-8 rounded-2xl p-5"
      >
        <div className="flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Design Credits</p>
          <Link
            to="/dashboard/credits"
            data-testid="overview-view-plan-link"
            className="text-[10px] font-semibold uppercase tracking-wider text-sky-400 transition-colors hover:text-sky-300"
          >
            View Plan
          </Link>
        </div>
        {outOfCredits ? (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold text-amber-200">You're out of design credits.</p>
            <Link
              to="/dashboard/credits"
              data-testid="overview-get-more-credits-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-4 py-2 text-xs font-bold text-[#1A1204] transition-colors hover:bg-amber-300"
            >
              <Wallet className="h-3.5 w-3.5" />
              Get More Credits
            </Link>
          </div>
        ) : (
          <>
            <p data-testid="overview-credits-remaining" className="mt-2 text-3xl font-extrabold tracking-tight text-white">
              {customer.credits_remaining}
              <span className="text-base font-bold text-slate-500"> / {customer.credits_total}</span>
            </p>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${100 - usedPct}%` }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="h-full rounded-full bg-gradient-to-r from-sky-400 to-amber-400"
              />
            </div>
          </>
        )}
        <p className="mt-2 text-xs text-slate-500">
          Credits are used when a design request enters production. Current plan:{" "}
          <span className="font-semibold text-slate-300">{customer.plan_name || PLAN_NAMES[customer.plan] || customer.plan}</span>.
        </p>
      </motion.div>

      {briefs.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.14 }}
          className="mt-10"
        >
          <EmptyState testId="first-time-state" eyebrow="Welcome to Grapho." text="Your creative workspace is ready.">
            <ol className="mx-auto max-w-sm space-y-2 text-left text-sm text-slate-300">
              <li>
                <span className="font-mono font-bold text-amber-300">01</span> Submit a brief
              </li>
              <li>
                <span className="font-mono font-bold text-amber-300">02</span> Our designers create it
              </li>
              <li>
                <span className="font-mono font-bold text-amber-300">03</span> Download your finished design
              </li>
            </ol>
            <Link
              to="/dashboard/new"
              data-testid="first-brief-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-[#1A1204] transition-colors hover:bg-amber-300"
            >
              <Sparkles className="h-4 w-4" />
              Submit Your First Brief
            </Link>
          </EmptyState>
        </motion.div>
      ) : (
        <>
          {activeBriefs.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.14 }}
              className="mt-10"
            >
              <h2 data-testid="overview-active-heading" className="flex items-center gap-2 text-lg font-bold tracking-tight text-white">
                <Sparkles className="h-4 w-4 text-amber-300" />
                Active Projects
              </h2>
              <div className="mt-4 space-y-3">
                {activeBriefs.map((b) => (
                  <ProjectRow key={b.brief_id} brief={b} />
                ))}
              </div>
              <Link
                to="/dashboard/projects"
                data-testid="overview-all-projects-link"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-400 transition-colors hover:text-sky-300"
              >
                View All Projects
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </motion.section>
          )}

          {deliveredBriefs.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-10"
            >
              <h2 data-testid="overview-deliveries-heading" className="flex items-center gap-2 text-lg font-bold tracking-tight text-white">
                <PackageCheck className="h-4 w-4 text-emerald-400" />
                Recent Deliveries
              </h2>
              <div className="mt-4 space-y-3">
                {deliveredBriefs.map((b) => (
                  <ProjectRow key={b.brief_id} brief={b} dateIso={b.delivered_at} dateLabel="Delivered" />
                ))}
              </div>
              <Link
                to="/dashboard/deliveries"
                data-testid="overview-all-deliveries-link"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-400 transition-colors hover:text-sky-300"
              >
                View All Deliveries
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </motion.section>
          )}
        </>
      )}
    </div>
  );
}
